import { Link, useLocation, useNavigate } from "react-router"
import { useState } from "react"
import type { Locale } from "../../content/types"
import { LOCALES } from "../../content/types"
import { getNavLinks, isActiveNav } from "./nav"
import { getPageByPath, getPagePath } from "../../content/registry"
import { ui } from "../../content/ui"

interface HeaderProps {
  locale: Locale | null
}

export default function Header({ locale }: HeaderProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  const currentLocale: Locale = locale ?? "nl"
  const navLinks = getNavLinks(currentLocale)

  /**
   * Language switching navigates to the equivalent localized URL of the
   * conceptual page (matched by registry id), never merely swaps client state.
   */
  const switchLanguage = (targetLocale: Locale) => {
    setIsLangMenuOpen(false)
    const currentPage = getPageByPath(location.pathname)
    const target =
      (currentPage && currentPage.path[targetLocale]) ||
      getPagePath(currentPage?.id ?? "home", targetLocale) ||
      getPagePath("home", targetLocale)!
    navigate(target)
  }

  return (
    <header className="fixed top-0 left-0 right-0 z-50 flex justify-center pt-4 px-4">
      <div className="w-full max-w-7xl bg-header-bg backdrop-blur-frosted border border-header-border rounded-header shadow-sm relative">
        <div className="flex items-center justify-between px-6 py-4">
          <Link
            to={getPagePath("home", currentLocale)!}
            className="flex items-center"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <img
              src="/SCHMEITZ_DE_GROEN.svg"
              alt="Schmeitz de Groen AI Labs"
              className="h-9 w-auto object-contain"
              width="180"
              height="36"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-6" aria-label={ui.breadcrumbs.ariaLabel[currentLocale]}>
            {navLinks.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`text-sm font-medium transition-all px-3 py-1.5 rounded-lg ${
                  isActiveNav(location.pathname, item.path)
                    ? "text-text-primary bg-button-secondary"
                    : "text-text-secondary hover:bg-button-secondary hover:text-text-primary"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            {/* Language Selector */}
            <div className="relative">
              <button
                onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                className="bg-button-secondary hover:bg-button-secondary-hover text-text-primary px-3 md:px-6 py-2.5 rounded-pill text-sm font-medium transition-colors flex items-center gap-2"
                aria-haspopup="menu"
                aria-expanded={isLangMenuOpen}
              >
                {currentLocale.toUpperCase()}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {isLangMenuOpen && (
                <div className="absolute right-0 mt-2 w-40 bg-card-bg border border-header-border rounded-lg shadow-lg overflow-hidden z-50">
                  {LOCALES.map((lang) => (
                    <button
                      key={lang}
                      onClick={() => switchLanguage(lang)}
                      lang={lang}
                      className={`w-full text-left px-4 py-2.5 text-sm font-medium transition-colors hover:bg-button-secondary-hover ${
                        currentLocale === lang ? "text-text-primary bg-button-secondary" : "text-text-secondary"
                      }`}
                    >
                      {ui.languageNames[lang]}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => {
                setIsMobileMenuOpen(!isMobileMenuOpen)
                setIsLangMenuOpen(false)
              }}
              className="md:hidden bg-button-secondary hover:bg-button-secondary-hover text-text-primary p-2.5 rounded-full transition-colors"
              aria-label="Toggle menu"
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {isMobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 mt-2 mx-6 p-4 bg-card-bg border border-header-border rounded-2xl shadow-xl md:hidden animate-in fade-in slide-in-from-top-5">
            <nav className="flex flex-col gap-2">
              {navLinks.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`text-base font-medium transition-all px-4 py-3 rounded-xl ${
                    isActiveNav(location.pathname, item.path)
                      ? "text-text-primary bg-button-secondary"
                      : "text-text-secondary hover:bg-button-secondary hover:text-text-primary"
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  )
}
